import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, ShieldCheck, ArrowLeft } from "lucide-react";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/></svg>
);

export default function Register() {
  const { registerVerified } = useAuth();
  const navigate = useNavigate();

  // step: "details" -> "otp"
  const [step, setStep] = useState("details");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendCode = async () => {
    setBusy(true);
    try {
      await api.post("/auth/send-otp", { email: email.toLowerCase().trim(), name });
      toast.success("Code sent to your email ♡");
      setStep("otp");
      setResendIn(30);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const submitDetails = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    await sendCode();
  };

  const submitOtp = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: email.toLowerCase().trim(), code: code.trim() });
      await registerVerified({ name, email: email.toLowerCase().trim(), phone: phone.trim(), password, otp_token: data.otp_token });
      toast.success("Welcome to Nailedit Beauty ♡");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify");
    } finally { setBusy(false); }
  };

  const resendCode = async () => {
    if (resendIn > 0) return;
    await sendCode();
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="register-page" className="min-h-screen flex items-center justify-center py-16 px-6 relative overflow-hidden">
      <div className="hero-orb" style={{ width: 600, height: 600, top: -200, right: -160, opacity: .5 }}/>
      <div className="surface p-10 w-full max-w-md relative z-[2]">
        {step === "details" && (
          <>
            <span className="eyebrow">Join the studio</span>
            <h1 className="text-[2.2rem] mt-3 mb-1">Create your account</h1>
            <p className="text-[var(--ink-soft)] text-sm">Get early drops &amp; 10% off your first set.</p>

            <form onSubmit={submitDetails} className="mt-7 space-y-4">
              <Input testid="reg-name" label="Name" value={name} onChange={(e) => setName(e.target.value)}/>
              <Input testid="reg-email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}/>
              <Input testid="reg-phone" label="Mobile (optional)" type="tel" value={phone} required={false} onChange={(e) => setPhone(e.target.value)}/>
              <Input testid="reg-password" label="Password (min 8)" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}/>
              <button type="submit" disabled={busy} data-testid="reg-submit" className="btn btn-primary w-full justify-center">
                {busy ? "Sending code..." : (<><Mail className="w-4 h-4"/> Send verification code</>)}
              </button>
            </form>

            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-[#fde0e8]"/>
              <span className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">or</span>
              <div className="flex-1 h-px bg-[#fde0e8]"/>
            </div>

            <button onClick={googleLogin} data-testid="google-register-button"
              className="w-full bg-white border border-[#fde0e8] hover:border-[var(--bloom)] py-3.5 rounded-full font-bold uppercase tracking-[0.14em] text-sm flex items-center justify-center gap-3 transition">
              <GoogleIcon/> Continue with Google
            </button>

            <p className="mt-6 text-sm text-center text-[var(--ink-soft)]">
              Already have an account? <Link to="/login" className="font-bold" style={{color:"var(--bloom)"}} data-testid="go-login-link">Sign in</Link>
            </p>
          </>
        )}

        {step === "otp" && (
          <>
            <button type="button" onClick={() => setStep("details")} data-testid="reg-back" className="text-xs uppercase tracking-[0.16em] font-bold text-[var(--ink-soft)] hover:text-[var(--bloom)] flex items-center gap-1 mb-4">
              <ArrowLeft className="w-3.5 h-3.5"/> Back
            </button>
            <span className="eyebrow">Verify your email</span>
            <h1 className="text-[2rem] mt-3 mb-1 leading-tight">Check your inbox ✨</h1>
            <p className="text-[var(--ink-soft)] text-sm">
              We sent a 6-digit code to <b style={{color:"var(--ink)"}}>{email}</b>. It expires in 10 minutes.
            </p>

            <form onSubmit={submitOtp} className="mt-7 space-y-5">
              <div>
                <span className="text-xs uppercase tracking-[0.16em] text-[var(--ink-soft)] font-bold">Enter code</span>
                <input
                  data-testid="reg-otp-code"
                  autoFocus
                  required
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-2 w-full bg-white border border-[#fde0e8] rounded-2xl px-5 py-4 text-center text-2xl font-serif tracking-[0.5em] focus:outline-none focus:border-[var(--bloom)]"
                  placeholder="••••••"
                />
              </div>
              <button type="submit" disabled={busy || code.length !== 6} data-testid="reg-otp-submit" className="btn btn-primary w-full justify-center disabled:opacity-60">
                {busy ? "Verifying..." : (<><ShieldCheck className="w-4 h-4"/> Verify &amp; create account</>)}
              </button>
            </form>

            <div className="mt-5 text-center text-xs text-[var(--ink-soft)]">
              Didn&apos;t get it?{" "}
              <button
                type="button"
                onClick={resendCode}
                disabled={resendIn > 0 || busy}
                data-testid="reg-otp-resend"
                className="font-bold disabled:opacity-40"
                style={{ color: "var(--bloom)" }}
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Input({ testid, label, required = true, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.16em] text-[var(--ink-soft)] font-bold">{label}</span>
      <input data-testid={testid} required={required} {...props}
        className="mt-1.5 w-full bg-white border border-[#fde0e8] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--bloom)]"/>
    </label>
  );
}
